/**
 * TouchControls — the on-screen stick and buttons, for playing this on a phone.
 *
 * THE PAD IS FIXED, VISIBLE, AND ABSOLUTE. It sits low at the bottom-left and does not move, so
 * a thumb can find it without looking; and the direction it is pushed is the direction the shark
 * goes, on screen, every time. Left is left.
 *
 * That last part is the whole design. It first behaved like the keyboard — a rudder, where the
 * stick's x axis was a rate of turn and its y axis was throttle — and pushing UP made the shark
 * swim forward rather than up the screen. On a keyboard that is right, because your hand is not
 * pointing at anything. A thumb IS pointing at something, and any control that ignores where it
 * is pointing feels broken within one second.
 *
 * Touching anywhere in the left half drives it, measured from the pad's fixed centre, so the pad
 * is a target rather than a thing you have to hit.
 *
 * TWO FINGERS ON THE WATER PINCH THE CAMERA, and where it is left is where it stays — across
 * sessions. A zoom that springs back on release is a zoom you have to hold, and holding a camera
 * setting with two fingers while also steering is not a thing anyone can do.
 *
 * On the WATER specifically: a finger that landed on the pad or on a button is already doing a
 * job and is never counted as half a gesture. Which is also why the pad's catchment is the
 * lower-left CORNER rather than the whole left half — a thumb never reaches the top of the
 * screen, and a zone that claimed it would leave nowhere to pinch.
 *
 * DEPTH IS BUTTONS, NOT THE SECOND AXIS OF A STICK. Diving is a sustained, deliberate thing —
 * you go down and you stay down — and a spring-loaded axis fights that: it takes constant
 * pressure to hold a depth, and letting go to do anything else surfaces you. Two buttons hold
 * their state for as long as a thumb rests on them, which is what the keyboard version does.
 *
 * Everything here writes into Input and nothing else. The player, the camera and the autopilot
 * never learn that a phone exists — which is also what lets the autopilot keep taking over when
 * the thumb comes off the glass, exactly as it does when a keyboard goes quiet.
 */
import type { Input, Action } from '../core/Input.ts';
import type { GameCamera } from '../render/Camera.ts';

const ZOOM_KEY = 'shoreplay.zoom.v1';

/**
 * How far the thumb travels for full deflection, in CSS pixels, and how the travel maps to
 * throttle.
 *
 * NOT the gamepad's curve, and that is the whole tuning problem here. A physical stick has
 * twenty millimetres of travel and a spring pulling it back, so squaring the magnitude costs
 * nothing — the rim is easy to reach and easy to hold. A thumb on glass has neither: it is
 * comfortable over about the first two thirds of any reasonable radius and has to stretch for
 * the rest. Squared, half a stick gave 0.18 throttle against a cruise of 11 u/s, which is a
 * shark that will not go anywhere unless you claw at the screen.
 *
 * 1.3 keeps real fine control near the centre and puts a proper cruise where a thumb rests.
 */
const STICK_RADIUS = 46;
const DEADZONE = 0.12;
const CURVE = 1.3;

/**
 * Where the pad sits: in from the left edge, and up from the bottom.
 *
 * Low, because a phone is held low in the hand and a thumb arcs from the bottom corner — a pad
 * placed at a comfortable-looking height on a desktop mockup is a stretch on the actual device.
 * The clearance is enough for the home indicator and the safe-area inset underneath it.
 */
const REST_X = 92;
const REST_UP = 96;

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
}

export class TouchControls {
  private readonly root: HTMLDivElement;
  private readonly stick: HTMLDivElement;
  private readonly knob: HTMLDivElement;
  private readonly input: Input;
  private readonly camera: GameCamera | null;

  /* Pinch. Tracked from WINDOW pointer events rather than from an element, because the two
     fingers may land anywhere — one on the pad, one on the water — and an element-scoped
     listener only ever sees the half of the gesture that happened over it. */
  private readonly touches = new Map<number, { x: number; y: number }>();
  /** pointers that landed on the pad or a button, and so can never be part of a pinch */
  private readonly onControl = new Set<number>();
  private pinchFrom = 0;
  private zoomFrom = 1;
  private pinching = false;

  private stickId = -1;
  private originX = 0;
  private originY = 0;

  constructor(parent: HTMLElement, input: Input, camera: GameCamera | null = null) {
    this.input = input;
    this.camera = camera;
    if (camera) {
      camera.setZoom(loadZoom());
      // Capture phase, so a gesture that starts on the pad is still seen as half a pinch.
      window.addEventListener('pointerdown', this.onAny, true);
      window.addEventListener('pointermove', this.onAny, true);
      window.addEventListener('pointerup', this.onAnyEnd, true);
      window.addEventListener('pointercancel', this.onAnyEnd, true);
    }

    this.root = mk('div', 'touch', parent);

    /* The zone is the left half: the region a thumb is allowed to land in. The stick itself
       rests at the bottom-left of it and moves to wherever a touch actually lands. */
    const zone = mk('div', 'touch-zone', this.root);
    this.stick = mk('div', 'touch-stick', this.root);
    mk('div', 'touch-stick-ring', this.stick);
    this.knob = mk('div', 'touch-knob', this.stick);
    this.rest();
    window.addEventListener('resize', this.rest);

    zone.addEventListener('pointerdown', this.onStickDown);
    zone.addEventListener('pointermove', this.onStickMove);
    zone.addEventListener('pointerup', this.onStickUp);
    zone.addEventListener('pointercancel', this.onStickUp);

    const pad = mk('div', 'touch-pad', this.root);
    this.button(pad, 'touch-btn is-rise', 'RISE', 'rise');
    this.button(pad, 'touch-btn is-dive', 'DIVE', 'dive');
    this.button(pad, 'touch-btn is-lunge', 'LUNGE', 'lunge');
  }

  dispose(): void {
    window.removeEventListener('resize', this.rest);
    window.removeEventListener('pointerdown', this.onAny, true);
    window.removeEventListener('pointermove', this.onAny, true);
    window.removeEventListener('pointerup', this.onAnyEnd, true);
    window.removeEventListener('pointercancel', this.onAnyEnd, true);
    this.root.remove();
    this.input.setTouchVector(0, 0);
  }

  private onAny = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;

    /* A finger that landed on a CONTROL is never half of a pinch.
       This is what made the depth buttons stop the shark: thumb on the pad plus a finger on
       DIVE is two touches, the pinch fired, and starting a pinch deliberately releases the pad
       — so pressing dive cancelled your steering. A pinch is a gesture on the WATER; a finger
       that arrived on a button or on the pad is already doing a job. */
    if (e.type === 'pointerdown') {
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('.touch-zone') || t.closest('.touch-btn'))) {
        this.onControl.add(e.pointerId);
        return;
      }
    }
    if (this.onControl.has(e.pointerId)) return;

    this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.touches.size !== 2) {
      if (this.touches.size < 2) this.pinching = false;
      return;
    }
    const [a, b] = [...this.touches.values()];
    const span = Math.hypot(a.x - b.x, a.y - b.y);
    if (!this.pinching) {
      this.pinching = true;
      this.pinchFrom = span;
      this.zoomFrom = this.camera ? this.camera.zoomLevel : 1;
      /* Drop the stick for the duration. One of these two fingers is probably the one that was
         steering, and a shark that veers off while you are adjusting the zoom is a shark you
         have to rescue afterwards. */
      this.stickId = -1;
      this.stick.classList.remove('is-on');
      this.knob.style.transform = 'translate(-50%, -50%)';
      this.input.setTouchVector(0, 0);
      return;
    }
    if (this.pinchFrom > 12 && span > 12 && this.camera) {
      // Fingers apart means zoom IN, so the distance goes down as the span goes up.
      this.camera.setZoom(this.zoomFrom * (this.pinchFrom / span));
    }
  };

  private onAnyEnd = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    this.onControl.delete(e.pointerId);
    this.touches.delete(e.pointerId);
    if (this.touches.size < 2 && this.pinching) {
      this.pinching = false;
      // Kept where it was left, and remembered next time. A zoom that springs back is a zoom
      // you have to hold, and nobody wants to hold a camera setting with two fingers.
      if (this.camera) saveZoom(this.camera.zoomLevel);
    }
  };

  /** park the stick where a left thumb rests, clear of the home indicator */
  private rest = (): void => {
    this.originX = REST_X;
    this.originY = window.innerHeight - REST_UP;
    this.stick.style.left = `${this.originX}px`;
    this.stick.style.top = `${this.originY}px`;
    this.knob.style.transform = 'translate(-50%, -50%)';
  };

  /**
   * A held button.
   *
   * Bound on pointerDOWN and released on up/cancel/leave rather than on click, because a click
   * only fires when the finger lifts — a dive button on click would be an instantaneous tap
   * that does nothing, since depth is a thing you hold.
   */
  private button(parent: HTMLElement, cls: string, label: string, action: Action): void {
    const b = mk('div', cls, parent);
    b.textContent = label;
    // Not a <button>: it must never take focus, or the browser paints a ring on the HUD and
    // the next key event goes to it instead of the game.
    b.setAttribute('role', 'button');
    b.setAttribute('aria-label', label);

    const set = (held: boolean) => (e: PointerEvent): void => {
      e.preventDefault();
      if (held) b.setPointerCapture(e.pointerId);
      else if (b.hasPointerCapture(e.pointerId)) b.releasePointerCapture(e.pointerId);
      b.classList.toggle('is-on', held);
      this.input.setTouchAction(action, held);
    };
    b.addEventListener('pointerdown', set(true));
    b.addEventListener('pointerup', set(false));
    b.addEventListener('pointercancel', set(false));
  }

  private onStickDown = (e: PointerEvent): void => {
    if (this.stickId !== -1) return;
    e.preventDefault();
    this.stickId = e.pointerId;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.stick.classList.add('is-on');
    // Measured from the pad's FIXED centre, so a touch that lands up and to the left is
    // immediately up and to the left rather than a new origin.
    this.drive(e.clientX, e.clientY);
  };


  private onStickMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.stickId) return;
    e.preventDefault();
    this.drive(e.clientX, e.clientY);
  };

  /** turn a touch point into a pointed direction, and show it on the knob */
  private drive(cx: number, cy: number): void {
    let dx = cx - this.originX;
    let dy = cy - this.originY;
    const len = Math.hypot(dx, dy);
    // Clamped to the rim, which is also what stops a diagonal being faster than a straight push.
    if (len > STICK_RADIUS) {
      dx = (dx / len) * STICK_RADIUS;
      dy = (dy / len) * STICK_RADIUS;
    }
    this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;

    const nx = dx / STICK_RADIUS;
    const ny = dy / STICK_RADIUS;
    const mag = Math.min(1, Math.hypot(nx, ny));
    if (mag < DEADZONE) {
      this.input.setTouchVector(0, 0);
      return;
    }
    /* Radial deadzone, then a curved magnitude: fine control near the centre, full drive at the
       rim. Direction is taken BEFORE the curve so the way you are pointing is never distorted
       by how hard you are pointing. */
    const t = (mag - DEADZONE) / (1 - DEADZONE);
    const r = Math.pow(t, CURVE);
    this.input.setTouchVector((nx / mag) * r, -(ny / mag) * r);
  }

  private onStickUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.stickId) return;
    this.stickId = -1;
    this.stick.classList.remove('is-on');
    this.knob.style.transform = 'translate(-50%, -50%)';
    // Released, so the autopilot takes back over — the same thing that happens when a keyboard
    // goes quiet.
    this.input.setTouchVector(0, 0);
  };
}

function loadZoom(): number {
  try {
    const raw = localStorage.getItem(ZOOM_KEY);
    const v = raw === null ? 1 : Number(raw);
    return Number.isFinite(v) ? v : 1;
  } catch {
    return 1;
  }
}

function saveZoom(z: number): void {
  try {
    localStorage.setItem(ZOOM_KEY, String(z));
  } catch {
    /* blocked storage — it just does not persist */
  }
}
